/* eslint-disable jsx-a11y/alt-text */
import React, { useCallback, useMemo, useState } from "react";
import Table from "Components/Common/Table/Table";
import { TableHeading } from "../TableHeading/TableHeading";
import { ReactComponent as ProfileImage } from "Assets/Profile.svg";
import { Toggle } from "Components/Common/ToggleBtn/Toggle";
import ClassNames from "Helpers/Common";
import { useAccessLevels } from "CustomHooks";
import { Button } from "Components/Common/Button/Button";
import { useBulkToggleAccessLevels } from "CustomHooks";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import { ErrorPopUp } from "Components/Common/PopUp/PopUp";
import { VisitTypesCell } from "./VisitTypesCell";

const HEADINGS = ["Doctor", "Specialty", "States", "Notes", "Skip Chat", "Visit Types", "Enabled"];

export const RestrictionsTable = ({ searchedProvider }) => {
  const { data: globalRestrictions, isLoading: globalRestrictionsLoading, refetch: refetchGlobal } = useAccessLevels(1, "", "ALL");

  const { data, isLoading, refetch, hasNext, hasPrevious, goToNextPage, goToPreviousPage } = useAccessLevels(1, searchedProvider);
  const globalAccessLevels = globalRestrictions?.result?.data || [];
  const individualAccessLevels = data?.result?.data || [];

  const accessLevels = [...globalAccessLevels, ...individualAccessLevels];
  const [editStatus, setEditStatus] = useState(false);
  const [updatedProvider, setUpdatedProvider] = useState({});
  const {
    mutate: saveAccessLevels,
    isLoading: saving,
    isError,
    error,
    reset,
  } = useBulkToggleAccessLevels(
    () => {},
    (error) => {
      console.log(error?.message, "Error");
    },
  );

  const onEditPress = useCallback(() => {
    setEditStatus(true);
  }, []);
  const onCancelPress = useCallback(() => {
    setEditStatus(false);
    setUpdatedProvider({});
  }, []);
  const onSavePress = () => {
    if (Object.keys(updatedProvider).length === 0) {
      alert("No changes to save");
      setEditStatus(false);
      return;
    }

    saveAccessLevels(updatedProvider, {
      onSuccess: async (response) => {
        console.log("Saved successfully!");
        setEditStatus(false);
        await refetch();
        await refetchGlobal();
        setUpdatedProvider({});
      },
      onError: (err) => {
        console.error("Update failed:", err);
      },
    });
  };
  /**
   * Return UI ready provider:
   * Original provider + updated overridden values
   */
  const getMergedProvider = (provider) => ({
    ...provider,
    ...(updatedProvider[provider.id] || {}),
  });

  /**
   * Update toggles
   */
  const handleToggle = (provider, key) => {
    const id = provider.id;

    const original = provider;
    const updated = updatedProvider[id] || {};

    // merge to know the current displayed value
    const merged = { ...original, ...updated };

    const newValue = !merged[key];

    // update only changed values
    const nextUpdated = {
      ...updated,
      [key]: newValue,
    };

    // Remove entries that match original values
    Object.keys(nextUpdated).forEach((field) => {
      if (nextUpdated[field] === original[field]) {
        delete nextUpdated[field];
      }
    });

    // If nothing changed → delete entry
    if (Object.keys(nextUpdated).length === 0) {
      setUpdatedProvider((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      return;
    }

    // Save updated values
    setUpdatedProvider((prev) => ({
      ...prev,
      [id]: nextUpdated,
    }));
  };
  // Non-boolean fields (enabledVisitTypes array/null) set the value directly
  const handleValueChange = (provider, key, value) => {
    setUpdatedProvider((prev) => ({
      ...prev,
      [provider.id]: { ...(prev[provider.id] || {}), [key]: value },
    }));
  };
  if (isLoading || saving)
    return (
      <div className="flex items-center justify-center h-64 ">
        <div className={`w-${10} h-${10} border-l-2 border-b-2 border-gray-400 rounded-full animate-spin`} />
      </div>
    );

  return (
    <>
      {isError && <ErrorPopUp error={error?.message} setApiError={reset} />}
      <div className="flex justify-between sm:justify-end items-center rounded-lg px-4 py-3 w-full sm:w-auto">
        <div className="flex gap-3">
          {/* Cancel */}
          <Button
            variant="Secondary"
            className="
            w-20
            font-normal
            text-sm
            py-1
            px-6
            rounded-md
      "
            btnText="Cancel"
            isDisabled={!editStatus}
            onClick={onCancelPress}
          />

          {/* Save / Edit */}
          <Button
            className="
            w-20
            font-normal
            text-sm
            py-1
            px-6
            inline-flex
            rounded-md
          text-white
      "
            btnText={editStatus ? "Save" : "Edit"}
            isDisabled={false}
            onClick={editStatus ? onSavePress : onEditPress}
          />
        </div>
      </div>

      <div className="p-4">
        <Table
          heading={<TableHeading TableHeader={HEADINGS} />}
          body={
            accessLevels.length > 0 ? (
              accessLevels.map((provider) => {
                const merged = getMergedProvider(provider);
                const isTogglesDisabled = !editStatus || !merged.active;

                const isGlobalAccessLevel = merged.accessLevel === "ALL";

                return (
                  <tr key={provider.id} className="border-b hover:bg-gray-50">
                    {/* Doctor */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="inline-block h-10 w-10 rounded-full overflow-hidden bg-gray-100">
                          <ProfileImage />
                        </span>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{isGlobalAccessLevel ? "GLOBAL" : merged.doctorName}</div>
                        </div>
                      </div>
                    </td>

                    {/* Specialty */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{merged?.specialties?.join(", ") || "-"}</td>

                    {/* States */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {merged?.stateRestrictions?.length
                        ? merged.stateRestrictions.map((state, i) => (
                            <span
                              key={i}
                              className={ClassNames(
                                "px-2 py-1 rounded text-xs font-medium mr-1",
                                state.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500",
                              )}
                            >
                              {state.shortStateName}
                            </span>
                          ))
                        : "-"}
                    </td>

                    {/* Notes */}
                    <td className="px-6 py-4 text-center">
                      <Toggle
                        enabled={merged.enableNotes}
                        disabled={isTogglesDisabled}
                        onChange={() => handleToggle(provider, "enableNotes")}
                      />
                    </td>

                    {/* Skip Chat */}
                    <td className="px-6 py-4 text-center">
                      <Toggle
                        enabled={merged.enableSkipChat}
                        disabled={isTogglesDisabled || merged.enableNotes === false}
                        onChange={() => handleToggle(provider, "enableSkipChat")}
                      />
                    </td>

                    {/* Visit types this doctor may see (independent of Notes) */}
                    <td className="px-6 py-4 text-center">
                      <VisitTypesCell
                        value={merged.enabledVisitTypes ?? null}
                        disabled={isTogglesDisabled}
                        onChange={(next) => handleValueChange(provider, "enabledVisitTypes", next)}
                      />
                    </td>

                    {/* Active / Enabled */}
                    <td className="px-6 py-4 text-center">
                      <Toggle enabled={merged.active} disabled={!editStatus} onChange={() => handleToggle(provider, "active")} />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-400">
                  No Providers Found
                </td>
              </tr>
            )
          }
        />
        <div className="mt-4">
          <PaginationBtns isNextDisabled={!hasNext} isPrevDisabled={!hasPrevious} onNext={goToNextPage} onPrevious={goToPreviousPage} />
        </div>
      </div>
    </>
  );
};
